import { css } from '@emotion/react'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import {
  SHomepageCallToAction,
  SHomepageCallToActionContent,
  SHomepageCallToActionContentBox,
  SHomepageCallToActionGallery,
  SHomepageCallToActionText,
} from './HomepageCallToAction.styled'
import { IImageGallery, ImageGallery } from 'components/ImageGallery'
import { Spacer } from 'components/spacer/Spacer'
import { StateSpinner } from 'components/StateSpinner'
import { Title, Title_h2_default, TTitleVariant } from 'components/Typography'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import { ReactElement, ReactNode } from 'react'
import { mutateAnalyticsData } from 'utils/analytics'
import { useAuth } from 'utils/auth/hooks/useAuth'
import { useLoadingCallback } from 'utils/hooks'

export function HomepageCallToAction(props: {
  title?: string
  titleVariant?: TTitleVariant
  images: IImageGallery['images']
  actionTitle?: string
  customAction?: ReactElement
  description?: ReactNode
  onClick?: () => void
  className?: string
  mirrored?: boolean
}) {
  const { t } = useTranslation('memory')
  const { isLogged } = useAuth()

  const [onClick, isLoading] = useLoadingCallback(async () => {
    if (!isLogged) {
      mutateAnalyticsData({ button: 'get_started_cta_lower' })
    }
    if (props.onClick) {
      await props.onClick()
    } else {
      await Router.push(isLogged ? '/me/dashboard' : '/signup')
    }
  })

  return (
    <Block className={props.className}>
      <SHomepageCallToAction mirrored={props.mirrored}>
        <SHomepageCallToActionContent>
          <SHomepageCallToActionContentBox>
            <Title variant={props.titleVariant || Title_h2_default} as="h2">
              {props.title ?? t('memory.singing.call_to_action.fallback_title')}
            </Title>

            <SHomepageCallToActionGallery className="mobile-only">
              <ImageGallery images={props.images} />
            </SHomepageCallToActionGallery>

            <Spacer size={[1, 1.825]} />

            <SHomepageCallToActionText>
              {props.description || t('memory.singing.call_to_action.fallback_desc')}
            </SHomepageCallToActionText>
            {props.customAction || (
              <StateSpinner isLoading={isLoading}>
                <Button
                  type="button"
                  size="large"
                  onClick={onClick}
                  data-testid="homepage-get-started"
                  css={css`
                    padding: 0 2.75rem;
                  `}
                  block={[true, false]}
                >
                  {props.actionTitle ?? t('memory.singing.call_to_action.fallback_button')}
                </Button>
              </StateSpinner>
            )}
          </SHomepageCallToActionContentBox>
        </SHomepageCallToActionContent>

        <SHomepageCallToActionGallery className="desktop-only">
          <ImageGallery images={props.images} />
        </SHomepageCallToActionGallery>
      </SHomepageCallToAction>
    </Block>
  )
}
